import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const LEAVE_COLORS = {
  off: "bg-blue-500",
  absent: "bg-red-500",
  half_day: "bg-amber-500",
};
const LEAVE_LABELS = { off: "Off", absent: "Absent", half_day: "Half" };

function monthDays(year, month) {
  const days = [];
  const last = new Date(year, month + 1, 0).getDate();
  for (let d = 1; d <= last; d++) days.push(new Date(year, month, d));
  return days;
}

const fmtDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export default function LeaveCalendar() {
  const [today] = useState(new Date());
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [users, setUsers] = useState([]);
  const [leaves, setLeaves] = useState([]);

  const days = useMemo(() => monthDays(year, month), [year, month]);
  const dateFrom = fmtDate(days[0]);
  const dateTo = fmtDate(days[days.length - 1]);

  const load = useCallback(async () => {
    const [u, l] = await Promise.all([
      api.get("/users"),
      api.get(`/leaves?date_from=${dateFrom}&date_to=${dateTo}`),
    ]);
    setUsers(u.data);
    setLeaves(l.data);
  }, [dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  // Build a lookup: { user_id: { date: leave } }
  const map = useMemo(() => {
    const m = {};
    for (const lv of leaves) {
      if (!m[lv.user_id]) m[lv.user_id] = {};
      m[lv.user_id][lv.date] = lv;
    }
    return m;
  }, [leaves]);

  const prev = () => {
    if (month === 0) { setYear(year - 1); setMonth(11); } else setMonth(month - 1);
  };
  const next = () => {
    if (month === 11) { setYear(year + 1); setMonth(0); } else setMonth(month + 1);
  };

  const monthLabel = new Date(year, month, 1).toLocaleString([], { month: "long", year: "numeric" });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <span className="label-overline">Month view</span>
          <h3 className="font-display text-2xl font-bold tracking-tight mt-1">{monthLabel}</h3>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={prev} data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></Button>
          <Button variant="outline" size="sm" onClick={next} data-testid="cal-next"><ChevronRight className="w-4 h-4" /></Button>
        </div>
      </div>

      <div className="flex gap-4 text-xs">
        {Object.entries(LEAVE_COLORS).map(([k, c]) => (
          <div key={k} className="flex items-center gap-1.5">
            <span className={`w-3 h-3 rounded-sm ${c}`} />
            <span className="text-zinc-600">{LEAVE_LABELS[k]}</span>
          </div>
        ))}
      </div>

      <div className="bg-white border border-zinc-200 rounded-md overflow-x-auto">
        <table className="text-xs min-w-full">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr>
              <th className="text-left py-2.5 px-3 sticky left-0 bg-zinc-50 z-10 min-w-[160px] uppercase text-[10px] tracking-widest text-zinc-500">Staff</th>
              {days.map((d) => {
                const isToday = fmtDate(d) === fmtDate(new Date());
                const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                return (
                  <th key={d.toISOString()} className={`py-2 px-1 text-center font-medium uppercase text-[10px] tracking-wide w-8 ${isWeekend ? "text-zinc-400" : "text-zinc-600"} ${isToday ? "bg-zinc-900 text-white rounded-sm" : ""}`}>
                    <div>{d.getDate()}</div>
                    <div className="text-[9px] font-normal">{d.toLocaleString([], { weekday: "narrow" })}</div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-zinc-100" data-testid={`cal-row-${u.id}`}>
                <td className="py-2 px-3 sticky left-0 bg-white z-10 whitespace-nowrap">
                  <div className="font-medium">{u.name}</div>
                  <div className="text-[10px] text-zinc-500 capitalize">{u.role.replace("_", " ")}</div>
                </td>
                {days.map((d) => {
                  const key = fmtDate(d);
                  const lv = map[u.id]?.[key];
                  return (
                    <td key={key} className="py-1 px-1 text-center">
                      {lv ? (
                        <span title={`${LEAVE_LABELS[lv.type]}${lv.note ? " · " + lv.note : ""}`} className={`inline-block w-5 h-5 rounded-sm ${LEAVE_COLORS[lv.type]}`} />
                      ) : (
                        <span className="inline-block w-5 h-5" />
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
            {users.length === 0 && (
              <tr><td colSpan={days.length + 1} className="py-10 text-center text-zinc-500">No staff to show.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
